// src/pages/Reset.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiUrl } from "../api";
import { useTranslation } from "react-i18next";
import { EyeInvisibleOutlined, EyeTwoTone, LockOutlined, LoginOutlined } from "@ant-design/icons";
import { Button, Divider, Input, message, Typography } from "antd";
import LanguageToggle from "../components/languageToggle";
import { useSearchParams } from "react-router-dom";

const Reset = () => {
  const [password, setPassword] = useState("");
  const [cpassword, setCpassword] = useState("");
  const [token, setToken] = useState("");
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<String | null>(null);
  const [apiLoading, setApiLoading] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const getPasswordStrength = (password: string): string => {
    let strength = 0;

    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return "Weak";
    if (strength === 3 || strength === 4) return "Medium";
    return "Strong";
  };

  const handleReset = () => {
    if (password.trim() === "") {
      setError(t("auth.reset.passwordEmpty"));
      return;
    }

    if (password !== cpassword) {
      setError(t("auth.reset.passwordsNotMatch"));
      return;
    }

    if (getPasswordStrength(password) !== "Strong") {
      setError(t("auth.reset.passwordWeak"));
      return;
    }

    setError(null);

    const ob = {
      token: token,
      newPassword: password,
    };

    setApiLoading(true);
    fetch(`${apiUrl}/user-password/reset-password`, {
      method: "POST",
      body: JSON.stringify(ob),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (response.status == 400) {
          setError(t("auth.reset.invalidToken"));
        } else if (response.status == 200) {
          message.success(t("auth.reset.success"));
          navigate("/neopaas/portal/login");
        }
        return response;
      })
      .then((res) => res)
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setApiLoading(false);
      });
  };

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");

    if (tokenFromUrl) {
      console.log("Token from URL:", tokenFromUrl);
      setToken(tokenFromUrl);
    } else {
      setError(t("auth.reset.invalidLink"));
    }
  }, [searchParams, t]);

  return (
    <div
      dir={i18n.language == "en" ? "ltr" : "rtl"}
      className={`h-screen flex items-center justify-center px-2 bg-gradient-to-tr from-neo-primary/30 to-neo-primary/0 `}
    >
      <div
        className={`bg-white p-8 shadow-sm rounded-md border-b-4 border-neo-primary/50 w-full md:w-[32rem] lg:w-[32rem] xl:w-[32rem]`}
      >
        <form>
          <div className="flex justify-between items-center">
            <Typography className="text-transparent bg-clip-text bg-gradient-to-r from-neo-primary to-neo-matt font-poppins-700 mb-4 text-3xl">
              NeoPaas
            </Typography>
            <LanguageToggle />
          </div>
          <Divider />
          <Typography className="font-poppins-500 text-xl mb-4">{t("auth.reset.title")}</Typography>
          <div className="mb-4">
            <label htmlFor="password" className="block mb-1 font-poppins-400 text-neo-primary ">
              {t("auth.reset.newPassword")}
            </label>
            <Input.Password
              id="password"
              prefix={<LockOutlined />}
              className="w-full p-2 border border-gray-300 rounded-md"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="cpassword" className="block mb-1 font-poppins-400 text-neo-primary ">
              {t("auth.reset.confirmNewPassword")}
            </label>
            <Input.Password
              id="cpassword"
              prefix={<LockOutlined />}
              className="w-full p-2 border border-gray-300 rounded-md"
              value={cpassword}
              onChange={(e) => setCpassword(e.target.value)}
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </div>

          <div className="h-10 ">
            {error && <Typography className="text-red-600 font-poppins-400 text-center">{error}</Typography>}
          </div>

          <Divider />
          <div className="flex gap-x-3 justify-end items-center ">
            <Button
              className="bg-gradient-to-tr from-neo-primary to-neo-primary/80 text-white"
              size="large"
              iconPosition="end"
              onClick={handleReset}
              icon={<LoginOutlined />}
              loading={apiLoading}
            >
              {t("auth.reset.resetButton")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Reset;
